import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import api from '../services/api';

/* =============================================================
   Lumix Óticas — DRE Clean Multiempresa
   Padrão visual: Google-like, DPI pequeno, limpo e premium.
   Foco: óticas, rede/unidades, CPV, despesas e resultado. Sem forçar empresaId pelo front.
============================================================= */

const MESES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez'
];

const PRESETS = [
  { id: 'year', label: 'Ano inteiro' },
  { id: 'today', label: 'Hoje' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês atual' },
  { id: 'lastMonth', label: 'Mês passado' },
  { id: 'last30', label: '30 dias' }
];

const DEFAULT_FINANCEIRO = {
  impostos: 0,
  custoArmacoes: 0,
  custoLentesLab: 0,
  custoOutros: 0,
  comissoes: 0,
  taxasCartao: 0,
  folhaPagamento: 0,
  aluguel: 0,
  marketing: 0,
  despesasGerais: 0
};

const Icons = {
  ArrowLeft: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  Refresh: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.5 2v6h-6" />
      <path d="M21.34 15.57a10 10 0 1 1-.59-9.21L21.5 8" />
    </svg>
  ),
  Close: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Save: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Plus: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Trash: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  Edit: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  Building: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 6h6M9 10h6M9 14h6M9 18h6" />
    </svg>
  ),
  Chart: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="5" />
      <rect x="12" y="8" width="3" height="9" />
      <rect x="17" y="5" width="3" height="12" />
    </svg>
  ),
  Alert: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Receipt: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2Z" />
      <path d="M8 7h8M8 12h8M8 17h5" />
    </svg>
  )
};

/* =============================================================
   Helpers
============================================================= */

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  let texto = String(value).trim();

  if (!texto) return 0;

  const negativo = /^-/.test(texto) || /\((.*)\)/.test(texto);

  texto = texto
    .replace(/R\$/gi, '')
    .replace(/\s/g, '')
    .replace(/[()]/g, '')
    .replace(/[^\d,.-]/g, '');

  const temVirgula = texto.includes(',');
  const temPonto = texto.includes('.');

  if (temVirgula && temPonto) {
    const ultimoPonto = texto.lastIndexOf('.');
    const ultimaVirgula = texto.lastIndexOf(',');

    if (ultimaVirgula > ultimoPonto) {
      texto = texto.replace(/\./g, '').replace(',', '.');
    } else {
      texto = texto.replace(/,/g, '');
    }
  } else if (temVirgula) {
    texto = texto.replace(',', '.');
  } else if (temPonto) {
    const partes = texto.split('.');

    if (partes.length > 2) {
      texto = partes.join('');
    }
  }

  const n = Number(texto);

  if (!Number.isFinite(n)) return 0;

  return negativo && n > 0 ? -n : n;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(parseNumber(value));

const formatCompactCurrency = (value) => {
  const n = parseNumber(value);

  if (Math.abs(n) >= 1000000) {
    return `R$ ${(n / 1000000).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })}M`;
  }

  if (Math.abs(n) >= 1000) {
    return `R$ ${(n / 1000).toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}k`;
  }

  return `R$ ${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

const formatInputNumber = (value) => {
  if (value === '' || value === null || value === undefined) return '';

  const n = parseNumber(value);

  if (!Number.isFinite(n)) return '';

  if (n === 0) return '';

  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const toInputDate = (dateString) => {
  if (!dateString) return '';

  try {
    return String(dateString).split('T')[0];
  } catch {
    return '';
  }
};

/*
  Multiempresa real:
  este componente NÃO força empresaId pelo localStorage.
  O backend deve resolver o escopo pelo token do usuário.
  Isso evita esconder vendas quando o usuário é admin/grupo/multiempresa
  ou quando o localStorage está com empresaId antigo/incompleto.
*/

const resolverArrayApi = (data, fallbackKey = '') => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.dados)) return data.dados;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.registros)) return data.registros;
  if (Array.isArray(data?.variables)) return data.variables;
  if (Array.isArray(data?.dre)) return data.dre;

  if (fallbackKey && Array.isArray(data?.[fallbackKey])) return data[fallbackKey];

  if (Array.isArray(data?.empresas)) return data.empresas;
  if (Array.isArray(data?.ordens)) return data.ordens;
  if (Array.isArray(data?.ordensServico)) return data.ordensServico;
  if (Array.isArray(data?.ordens_servico)) return data.ordens_servico;

  if (data?.data && typeof data.data === 'object') {
    const nested = resolverArrayApi(data.data, fallbackKey);

    if (nested.length) return nested;
  }

  return [];
};

const normalizarId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

const mesmoId = (a, b) => {
  if (!a || !b) return false;
  return String(a) === String(b);
};

const normalizarTexto = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const safeId = (value = '') =>
  String(value || 'loja')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '_');

const getEmpresaCodigo = (empresa = {}) =>
  String(empresa?.codigoLoja || empresa?.loja || empresa?.unidade || empresa?._id || empresa?.id || '').trim();

const getEmpresaLabel = (empresa = {}) => {
  if (!empresa) return '';

  const nome = empresa.nome || empresa.razaoSocial || 'Empresa';
  const unidade = empresa.unidade && empresa.unidade !== 'Matriz' ? ` · ${empresa.unidade}` : '';

  return `${nome}${unidade}`;
};

const getEmpresaIdOS = (os = {}) =>
  normalizarId(
    os.empresaId ||
      os.empresa?._id ||
      os.empresa?.id ||
      os.empresa ||
      ''
  );

const encontrarEmpresaDaOS = (os = {}, empresas = []) => {
  const osEmpresaId = getEmpresaIdOS(os);
  const loja = String(os.loja || '').trim();
  const lojaNormalizada = normalizarTexto(loja);

  return (
    empresas.find((empresa) => mesmoId(empresa._id, osEmpresaId) || mesmoId(empresa.id, osEmpresaId)) ||
    empresas.find((empresa) => normalizarTexto(getEmpresaCodigo(empresa)) === lojaNormalizada) ||
    empresas.find((empresa) => normalizarTexto(empresa.unidade) === lojaNormalizada) ||
    empresas.find((empresa) => normalizarTexto(empresa.nome) === lojaNormalizada) ||
    empresas.find((empresa) => {
      const alvo = [
        empresa.codigoLoja,
        empresa.loja,
        empresa.unidade,
        empresa.nome,
        empresa.razaoSocial
      ].map(normalizarTexto).filter(Boolean).join(' ');

      return Boolean(lojaNormalizada && alvo && (alvo.includes(lojaNormalizada) || lojaNormalizada.includes(alvo)));
    }) ||
    null
  );
};

const getDataOS = (os = {}) =>
  os.dataCompra ||
  os.dataVenda ||
  os.data ||
  os.dataEntrada ||
  os.createdAt ||
  os.created_at ||
  os.updatedAt ||
  '';

const getOsItems = (os = {}) => {
  if (Array.isArray(os.itens) && os.itens.length) return os.itens;

  if (os.produto || os.receita || os.paciente) {
    return [
      {
        produto: os.produto || {},
        quantidade: 1,
        valorTotal: os.valorTotal || os.valor || 0
      }
    ];
  }

  return [];
};

const getValorOS = (os = {}) => {
  const camposDiretos = [
    os.valorTotal,
    os.total,
    os.valor,
    os.valorVenda,
    os.valorFinal,
    os.totalVenda,
    os.totalGeral,
    os.financeiro?.valorTotal,
    os.financeiro?.total,
    os.resumo?.valorTotal,
    os.resumo?.total
  ];

  for (const campo of camposDiretos) {
    const valor = parseNumber(campo);

    if (valor > 0) return valor;
  }

  const totalItens = getOsItems(os).reduce((acc, item) => {
    const valorTotalItem = parseNumber(
      item.valorTotal ||
      item.total ||
      item.subtotal ||
      item.produto?.valorTotal ||
      item.produto?.preco
    );

    if (valorTotalItem > 0) return acc + valorTotalItem;

    const valorUnitario = parseNumber(item.valorUnitario || item.produto?.valorUnitario);
    const quantidade = Number(item.quantidade || 1) || 1;

    return acc + valorUnitario * quantidade;
  }, 0);

  if (totalItens > 0) {
    return Number(totalItens.toFixed(2));
  }

  if (Array.isArray(os.pagamentos)) {
    const totalPago = os.pagamentos.reduce((acc, p) => acc + parseNumber(p.valor), 0);

    if (totalPago > 0) return Number(totalPago.toFixed(2));
  }

  return 0;
};

const getYearRange = (year) => ({
  start: `${year}-01-01`,
  end: `${year}-12-31`
});



const getPresetRange = (preset, year) => {
  const now = new Date();
  const yearStart = new Date(`${year}-01-01T00:00:00`);
  const yearEnd = new Date(`${year}-12-31T23:59:59`);

  let start = new Date(yearStart);
  let end = new Date(yearEnd);

  if (preset === 'today') {
    start = new Date(now);
    end = new Date(now);
  }

  if (preset === 'week') {
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    start = new Date(now);
    start.setDate(now.getDate() + diffToMon);
    end = new Date(now);
  }

  if (preset === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now);
  }

  if (preset === 'lastMonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 0);
  }

  if (preset === 'last30') {
    start = new Date(now);
    start.setDate(now.getDate() - 29);
    end = new Date(now);
  }

  if (preset === 'year') {
    start = new Date(yearStart);
    end = new Date(yearEnd);
  }

  if (start < yearStart) start = yearStart;
  if (end > yearEnd) end = yearEnd;

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
};

const calcularDreMes = (mes) => {
  const receitaLiquida = mes.receitaBruta - mes.impostos;
  const cpv = mes.custoArmacoes + mes.custoLentesLab + mes.custoOutros;
  const lucroBruto = receitaLiquida - cpv;

  const despesasOperacionais =
    mes.comissoes +
    mes.taxasCartao +
    mes.folhaPagamento +
    mes.aluguel +
    mes.marketing +
    mes.despesasGerais +
    mes.totalVariaveis;

  const lucroLiquido = lucroBruto - despesasOperacionais;
  const margemLucro = mes.receitaBruta > 0 ? (lucroLiquido / mes.receitaBruta) * 100 : 0;
  const margemBruta = mes.receitaLiquida > 0 ? (lucroBruto / mes.receitaLiquida) * 100 : 0;
  const cpvPercent = mes.receitaBruta > 0 ? (cpv / mes.receitaBruta) * 100 : 0;

  return {
    ...mes,
    receitaLiquida,
    cpv,
    lucroBruto,
    despesasOperacionais,
    lucroLiquido,
    margemLucro,
    margemBruta,
    cpvPercent
  };
};

const soma = (arr, key) => arr.reduce((acc, item) => acc + Number(item[key] || 0), 0);

/* =============================================================
   Componentes pequenos
============================================================= */

function ThinLoader({ label = 'Carregando DRE...' }) {
  return (
    <div className="dre-loading">
      <div className="thin-loader" />
      <span>{label}</span>
    </div>
  );
}

function KpiCell({ icon, label, value, help, tone = 'neutral' }) {
  return (
    <div className={`kpi-cell ${tone}`}>
      <div className="kpi-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
      {help ? <small>{help}</small> : null}
    </div>
  );
}

function AccountRow({ label, values, total, className = '', isNegative = false, onMonthClick }) {
  return (
    <tr className={className}>
      <td className="sticky-col">{label}</td>

      {values.map((value, index) => (
        <td key={`${label}-${index}`} onClick={onMonthClick ? () => onMonthClick(index) : undefined}>
          <span className={isNegative && value > 0 ? 'negative' : value < 0 ? 'negative' : ''}>
            {formatCurrency(value)}
          </span>
        </td>
      ))}

      <td className="total-col">
        <span className={isNegative && total > 0 ? 'negative' : total < 0 ? 'negative' : ''}>
          {formatCurrency(total)}
        </span>
      </td>
    </tr>
  );
}

function PercentRow({ label, values, total, onMonthClick }) {
  return (
    <tr className="row-percent">
      <td className="sticky-col">{label}</td>

      {values.map((value, index) => (
        <td key={`${label}-${index}`} onClick={onMonthClick ? () => onMonthClick(index) : undefined}>
          <span className={value >= 15 ? 'positive' : value >= 0 ? 'warning' : 'negative'}>
            {Number(value || 0).toFixed(1)}%
          </span>
        </td>
      ))}

      <td className="total-col">
        <span className={total >= 15 ? 'positive' : total >= 0 ? 'warning' : 'negative'}>
          {Number(total || 0).toFixed(1)}%
        </span>
      </td>
    </tr>
  );
}

/* =============================================================
   Componente principal
============================================================= */

export default function Dre({ onBack }) {
  const anoAtual = new Date().getFullYear();

  const [ordens, setOrdens] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [despesasDB, setDespesasDB] = useState([]);
  const [variaveis, setVariaveis] = useState([]);
  const [variaveisMesValores, setVariaveisMesValores] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [anoSelecionado, setAnoSelecionado] = useState(String(anoAtual));
  const [empresaSelecionada, setEmpresaSelecionada] = useState('Todas');

  const [dateInicio, setDateInicio] = useState(`${anoAtual}-01-01`);
  const [dateFim, setDateFim] = useState(`${anoAtual}-12-31`);
  const [activePreset, setActivePreset] = useState('year');

  const [modalMesAberto, setModalMesAberto] = useState(false);
  const [dadosMesEdit, setDadosMesEdit] = useState(null);

  const [modalVariavelAberto, setModalVariavelAberto] = useState(false);
  const [variavelEdit, setVariavelEdit] = useState(null);

  const empresasComFallback = useMemo(() => {
    const cadastradas = empresas.filter((empresa) => empresa.ativo !== false);
    const resultado = [...cadastradas];

    const chavesExistentes = new Set(
      cadastradas.flatMap((empresa) => [
        normalizarId(empresa._id || empresa.id),
        normalizarTexto(getEmpresaCodigo(empresa)),
        normalizarTexto(empresa.unidade),
        normalizarTexto(empresa.nome)
      ]).filter(Boolean)
    );

    ordens.forEach((os) => {
      if (encontrarEmpresaDaOS(os, cadastradas)) return;

      const loja = String(os.loja || getEmpresaIdOS(os) || '').trim();

      if (!loja) return;

      const chave = normalizarTexto(loja);

      if (!chave || chavesExistentes.has(chave)) return;

      chavesExistentes.add(chave);

      resultado.push({
        _id: `fallback-${safeId(chave)}`,
        id: `fallback-${safeId(chave)}`,
        nome: loja,
        unidade: loja,
        codigoLoja: loja,
        ativo: true,
        fallback: true
      });
    });

    return resultado;
  }, [empresas, ordens]);

  const empresaAtualObj = useMemo(() => {
    return empresasComFallback.find((empresa) => {
      return (
        mesmoId(empresa._id, empresaSelecionada) ||
        mesmoId(empresa.id, empresaSelecionada) ||
        getEmpresaCodigo(empresa) === empresaSelecionada
      );
    }) || null;
  }, [empresasComFallback, empresaSelecionada]);

  const empresaAtualLabel = empresaSelecionada === 'Todas'
    ? 'Consolidado da rede'
    : getEmpresaLabel(empresaAtualObj || { nome: empresaSelecionada });

  const empresaAtualCodigo = empresaSelecionada === 'Todas'
    ? 'Todas'
    : getEmpresaCodigo(empresaAtualObj || {}) || empresaSelecionada;

  /*
    IMPORTANTE:
    Mantemos dependências primitivas aqui para não gerar loop.
    Se usar o objeto inteiro empresaAtualObj no useEffect/useCallback,
    cada GET /empresas recria objetos novos e a DRE fica buscando sem parar.
  */
  const empresaAtualId = empresaSelecionada === 'Todas'
    ? ''
    : normalizarId(empresaAtualObj?._id || empresaAtualObj?.id || '');

  const dreLojaParam = empresaSelecionada === 'Todas'
    ? 'Todas'
    : empresaAtualCodigo;

  const empresaAtualEhFallback = Boolean(empresaAtualObj?.fallback);

  const dreParams = useMemo(() => {
    const params = {
      ano: anoSelecionado,
      loja: dreLojaParam,
      store: dreLojaParam
    };

    if (empresaSelecionada !== 'Todas' && empresaAtualId && !empresaAtualEhFallback) {
      params.empresaId = empresaAtualId;
    }

    return params;
  }, [anoSelecionado, empresaSelecionada, dreLojaParam, empresaAtualId, empresaAtualEhFallback]);

  const fetchEmpresas = useCallback(async () => {
    try {
      const response = await api.get('/empresas', {
        params: {
          comResumo: true
        }
      });

      const lista = resolverArrayApi(response.data, 'empresas').filter((empresa) => empresa.ativo !== false);

      setEmpresas(lista);
    } catch (error) {
      console.error('[DRE] Erro ao carregar empresas:', error);
      setEmpresas([]);
    }
  }, []);

  const fetchOrdens = useCallback(async () => {
    const response = await api.get('/ordens-servico');

    setOrdens(resolverArrayApi(response.data, 'ordens'));
  }, []);

  const fetchDre = useCallback(async () => {
    const response = await api.get('/dre', {
      params: dreParams
    });

    setDespesasDB(resolverArrayApi(response.data, 'dre'));
  }, [dreParams]);

  const fetchVariaveis = useCallback(async () => {
    try {
      const response = await api.get('/variables', {
        params: {
          year: Number(anoSelecionado),
          store: dreParams.store,
          loja: dreParams.loja,
          empresaId: dreParams.empresaId
        }
      });

      const lista = resolverArrayApi(response.data, 'variables');

      setVariaveis(lista);

      const valores = {};

      lista.forEach((variavel) => {
        if (variavel.isGlobal !== false) {
          valores[variavel._id] = {};

          for (let month = 1; month <= 12; month += 1) {
            valores[variavel._id][month] = parseNumber(variavel.value);
          }
        }

        if (variavel.month) {
          valores[variavel._id] = {
            ...(valores[variavel._id] || {}),
            [Number(variavel.month)]: parseNumber(variavel.value)
          };
        }

        if (variavel.valuesByMonth && typeof variavel.valuesByMonth === 'object') {
          valores[variavel._id] = {
            ...(valores[variavel._id] || {})
          };

          Object.entries(variavel.valuesByMonth).forEach(([monthKey, monthValue]) => {
            valores[variavel._id][monthKey] = parseNumber(monthValue);
          });
        }
      });

      setVariaveisMesValores(valores);
    } catch (error) {
      console.warn('[DRE] Erro ao carregar variáveis:', error);
      setVariaveis([]);
      setVariaveisMesValores({});
    }
  }, [anoSelecionado, dreParams]);

  /*
    Busca inicial: empresas e OS.
    Não misture GET /empresas com GET /dre no mesmo efeito dependente da loja,
    senão a troca de loja pode recriar objetos e disparar loop.
  */
  useEffect(() => {
    let ativo = true;

    async function carregarBaseInicial() {
      setLoading(true);

      try {
        await Promise.all([
          fetchEmpresas(),
          fetchOrdens()
        ]);
      } catch (error) {
        console.error('[DRE] Erro ao carregar base inicial:', error);
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    }

    carregarBaseInicial();

    return () => {
      ativo = false;
    };
  }, [fetchEmpresas, fetchOrdens]);

  /*
    Busca da DRE e variáveis:
    roda apenas quando ano/loja/empresa realmente mudam.
  */
  useEffect(() => {
    let ativo = true;

    async function carregarDreEVariaveis() {
      setLoading(true);

      try {
        await Promise.all([
          fetchDre(),
          fetchVariaveis()
        ]);
      } catch (error) {
        console.error('[DRE] Erro ao carregar DRE/variáveis:', error);
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    }

    carregarDreEVariaveis();

    return () => {
      ativo = false;
    };
  }, [fetchDre, fetchVariaveis]);

  useEffect(() => {
    const range = getYearRange(anoSelecionado);

    setDateInicio(range.start);
    setDateFim(range.end);
    setActivePreset('year');
  }, [anoSelecionado]);

  const fetchAll = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        fetchEmpresas(),
        fetchOrdens(),
        fetchDre(),
        fetchVariaveis()
      ]);
    } catch (error) {
      console.error('[DRE] Erro ao atualizar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchEmpresas, fetchOrdens, fetchDre, fetchVariaveis]);

  const empresasFiltro = useMemo(() => {
    return empresasComFallback.map((empresa) => ({
      id: normalizarId(empresa._id || empresa.id) || getEmpresaCodigo(empresa),
      codigo: getEmpresaCodigo(empresa),
      label: getEmpresaLabel(empresa)
    }));
  }, [empresasComFallback]);

  const variaveisFiltradas = useMemo(() => {
    return variaveis.filter((v) => {
      const yearOk = !v.year || Number(v.year) === Number(anoSelecionado);
      const store = String(v.store || v.loja || '').trim();
      const empresaId = normalizarId(v.empresaId);

      const lojaOk =
        empresaSelecionada === 'Todas'
          ? true
          : store === empresaAtualCodigo ||
            store === empresaSelecionada ||
            (!empresaAtualEhFallback && mesmoId(empresaId, empresaAtualId));

      return yearOk && lojaOk && v.isGlobal !== false;
    });
  }, [variaveis, anoSelecionado, empresaSelecionada, empresaAtualCodigo, empresaAtualId, empresaAtualEhFallback]);

  const dreData = useMemo(() => {
    const mesesBase = Array.from({ length: 12 }, (_, index) => ({
      mes: MESES[index],
      mesIndex: index,
      mesNumero: index + 1,
      receitaBruta: 0,
      qtdOS: 0,
      ticketMedio: 0,
      ...DEFAULT_FINANCEIRO,
      totalVariaveis: 0,
      variaveisDetalhe: {}
    }));

    ordens.forEach((os) => {
      const dataStr = getDataOS(os);
      if (!dataStr) return;

      const data = new Date(dataStr);
      if (Number.isNaN(data.getTime())) return;

      if (data.getFullYear() !== Number(anoSelecionado)) return;

      const iso = toInputDate(dataStr);

      if (dateInicio && iso && iso < dateInicio) return;
      if (dateFim && iso && iso > dateFim) return;

      if (empresaSelecionada !== 'Todas') {
        const empresaDaOS = encontrarEmpresaDaOS(os, empresasComFallback);
        const osEmpresaId = getEmpresaIdOS(os);
        const loja = String(os.loja || '').trim();

        const matchEmpresa =
          mesmoId(osEmpresaId, empresaSelecionada) ||
          mesmoId(empresaDaOS?._id, empresaSelecionada) ||
          mesmoId(empresaDaOS?.id, empresaSelecionada);

        const matchCodigo =
          loja === empresaSelecionada ||
          loja === empresaAtualCodigo ||
          getEmpresaCodigo(empresaDaOS || {}) === empresaAtualCodigo;

        if (!matchEmpresa && !matchCodigo) return;
      }

      const mes = mesesBase[data.getMonth()];
      const valor = getValorOS(os);

      if (valor > 0) {
        mes.receitaBruta += valor;
        mes.qtdOS += 1;
      }
    });

    mesesBase.forEach((mes) => {
      mes.ticketMedio = mes.qtdOS > 0 ? mes.receitaBruta / mes.qtdOS : 0;

      const dreMesBanco = despesasDB.find((d) => Number(d.mes) === mes.mesNumero);

      if (dreMesBanco?.financeiro) {
        Object.keys(DEFAULT_FINANCEIRO).forEach((key) => {
          mes[key] = parseNumber(dreMesBanco.financeiro[key]);
        });
      }

      /*
        IMPORTANTE:
        O valor mensal real das variáveis é salvo dentro do documento da DRE,
        em dreMesBanco.variaveisMes, no formato:
        {
          "idDaVariavel": valor
        }

        Antes a tabela olhava apenas para /variables e para o value base da variável.
        Por isso salvava no Mongo, mas não aparecia na tabela após recarregar.
      */
      const variaveisMesBanco = dreMesBanco?.variaveisMes || {};

      variaveisFiltradas.forEach((variavel) => {
        const variavelId = normalizarId(variavel._id || variavel.id);

        const valor =
          variaveisMesBanco?.[variavelId] ??
          variaveisMesBanco?.[String(variavelId)] ??
          variaveisMesValores[variavelId]?.[mes.mesNumero] ??
          variaveisMesValores[variavelId]?.[String(mes.mesNumero)] ??
          variavel.value ??
          0;

        const valorNum = parseNumber(valor);

        mes.totalVariaveis += valorNum;
        mes.variaveisDetalhe[variavelId] = valorNum;
      });
    });

    const mesesCalculados = mesesBase.map(calcularDreMes);

    const kpi = {
      receitaTotal: soma(mesesCalculados, 'receitaBruta'),
      impostos: soma(mesesCalculados, 'impostos'),
      receitaLiquida: soma(mesesCalculados, 'receitaLiquida'),
      cpv: soma(mesesCalculados, 'cpv'),
      lucroBruto: soma(mesesCalculados, 'lucroBruto'),
      despesasOperacionais: soma(mesesCalculados, 'despesasOperacionais'),
      lucroLiquido: soma(mesesCalculados, 'lucroLiquido'),
      qtdOS: soma(mesesCalculados, 'qtdOS')
    };

    kpi.ticketMedio = kpi.qtdOS > 0 ? kpi.receitaTotal / kpi.qtdOS : 0;
    kpi.margemLiquida = kpi.receitaTotal > 0 ? (kpi.lucroLiquido / kpi.receitaTotal) * 100 : 0;
    kpi.margemBruta = kpi.receitaLiquida > 0 ? (kpi.lucroBruto / kpi.receitaLiquida) * 100 : 0;
    kpi.cpvPercent = kpi.receitaTotal > 0 ? (kpi.cpv / kpi.receitaTotal) * 100 : 0;

    return {
      meses: mesesCalculados,
      kpi
    };
  }, [
    ordens,
    despesasDB,
    anoSelecionado,
    dateInicio,
    dateFim,
    empresaSelecionada,
    empresaAtualCodigo,
    empresasComFallback,
    variaveisFiltradas,
    variaveisMesValores
  ]);

  const { meses, kpi } = dreData;

  const chartData = useMemo(() => {
    return meses.map((mes) => ({
      ...mes,
      receitaBruta: Number(mes.receitaBruta.toFixed(2)),
      lucroLiquido: Number(mes.lucroLiquido.toFixed(2)),
      margemLucro: Number(mes.margemLucro.toFixed(2))
    }));
  }, [meses]);

  const abrirDetalhesMes = (mesData) => {
    if (empresaSelecionada === 'Todas') {
      alert('Selecione uma ótica específica para lançar despesas e variáveis.');
      return;
    }

    const variaveisInputs = {};
    const variaveisInputsRaw = {};

    variaveisFiltradas.forEach((variavel) => {
      const variavelId = normalizarId(variavel._id || variavel.id);

      const valor =
        mesData.variaveisDetalhe?.[variavelId] ??
        mesData.variaveisDetalhe?.[String(variavelId)] ??
        variaveisMesValores[variavelId]?.[mesData.mesNumero] ??
        variaveisMesValores[variavelId]?.[String(mesData.mesNumero)] ??
        variavel.value ??
        0;

      variaveisInputs[variavelId] = parseNumber(valor);
      variaveisInputsRaw[variavelId] = formatInputNumber(valor);
    });

    setDadosMesEdit({
      ...mesData,
      impostosInput: formatInputNumber(mesData.impostos),
      custoArmacoesInput: formatInputNumber(mesData.custoArmacoes),
      custoLentesLabInput: formatInputNumber(mesData.custoLentesLab),
      custoOutrosInput: formatInputNumber(mesData.custoOutros),
      comissoesInput: formatInputNumber(mesData.comissoes),
      taxasCartaoInput: formatInputNumber(mesData.taxasCartao),
      folhaPagamentoInput: formatInputNumber(mesData.folhaPagamento),
      aluguelInput: formatInputNumber(mesData.aluguel),
      marketingInput: formatInputNumber(mesData.marketing),
      despesasGeraisInput: formatInputNumber(mesData.despesasGerais),
      variaveisInputs,
      variaveisInputsRaw
    });

    setModalMesAberto(true);
  };

  const recalcularModal = (base) => {
    const calculado = calcularDreMes(base);

    return {
      ...base,
      ...calculado
    };
  };

  const handleInputChange = (campo, valor) => {
    setDadosMesEdit((prev) => {
      const next = {
        ...prev,
        [campo]: parseNumber(valor),
        [`${campo}Input`]: valor
      };

      return recalcularModal(next);
    });
  };

  const handleVariavelModalChange = (id, valor) => {
    setDadosMesEdit((prev) => {
      const variaveisInputs = {
        ...(prev.variaveisInputs || {}),
        [id]: parseNumber(valor)
      };

      const variaveisInputsRaw = {
        ...(prev.variaveisInputsRaw || {}),
        [id]: valor
      };

      const totalVariaveis = Object.values(variaveisInputs).reduce((acc, v) => acc + Number(v || 0), 0);

      return recalcularModal({
        ...prev,
        variaveisInputs,
        variaveisInputsRaw,
        totalVariaveis
      });
    });
  };

  const salvarDreMes = async () => {
    if (!dadosMesEdit) return;

    setSaving(true);

    try {
      setVariaveisMesValores((prev) => {
        const next = { ...prev };

        Object.entries(dadosMesEdit.variaveisInputs || {}).forEach(([id, value]) => {
          next[id] = {
            ...(next[id] || {}),
            [dadosMesEdit.mesNumero]: parseNumber(value)
          };
        });

        return next;
      });

      const payload = {
        ano: Number(anoSelecionado),
        mes: dadosMesEdit.mesNumero,
        loja: empresaAtualCodigo,
        store: empresaAtualCodigo,
        empresaId: empresaAtualEhFallback ? undefined : (empresaAtualId || empresaSelecionada),
        financeiro: {
          impostos: parseNumber(dadosMesEdit.impostos),
          custoArmacoes: parseNumber(dadosMesEdit.custoArmacoes),
          custoLentesLab: parseNumber(dadosMesEdit.custoLentesLab),
          custoOutros: parseNumber(dadosMesEdit.custoOutros),
          comissoes: parseNumber(dadosMesEdit.comissoes),
          taxasCartao: parseNumber(dadosMesEdit.taxasCartao),
          folhaPagamento: parseNumber(dadosMesEdit.folhaPagamento),
          aluguel: parseNumber(dadosMesEdit.aluguel),
          marketing: parseNumber(dadosMesEdit.marketing),
          despesasGerais: parseNumber(dadosMesEdit.despesasGerais)
        },
        variaveisMes: Object.fromEntries(
          Object.entries(dadosMesEdit.variaveisInputs || {}).map(([id, value]) => [id, parseNumber(value)])
        )
      };

      await api.post('/dre/mes', payload);

      setModalMesAberto(false);
      setDadosMesEdit(null);

      await fetchDre();
      await fetchVariaveis();
    } catch (error) {
      console.error('[DRE] Erro ao salvar mês:', error);
      alert(error.response?.data?.error || 'Não foi possível salvar os lançamentos da DRE.');
    } finally {
      setSaving(false);
    }
  };

  const salvarVariavel = async () => {
    if (empresaSelecionada === 'Todas') {
      alert('Selecione uma ótica específica para criar variável.');
      return;
    }

    const nome = String(variavelEdit?.name || '').trim();

    if (!nome) {
      alert('Informe o nome da variável.');
      return;
    }

    const valorBase = parseNumber(
      variavelEdit?.valueInput ?? variavelEdit?.value ?? 0
    );

    setSaving(true);

    try {
      const payload = {
        name: nome,
        value: valorBase,
        description: String(variavelEdit?.description || '').trim(),
        year: Number(anoSelecionado),
        month: null,
        store: empresaAtualCodigo,
        loja: empresaAtualCodigo,
        empresaId: empresaAtualEhFallback ? undefined : (empresaAtualId || empresaSelecionada),
        isGlobal: true
      };

      if (variavelEdit?._id) {
        await api.put(`/variables/${variavelEdit._id}`, payload);
      } else {
        await api.post('/variables', payload);
      }

      setModalVariavelAberto(false);
      setVariavelEdit(null);
      await fetchVariaveis();
    } catch (error) {
      console.error('[DRE] Erro ao salvar variável:', error);
      alert(error.response?.data?.error || 'Não foi possível salvar a variável.');
    } finally {
      setSaving(false);
    }
  };

  const excluirVariavel = async (id) => {
    if (!id) return;
    if (!window.confirm('Deseja remover esta variável da DRE?')) return;

    setSaving(true);

    try {
      await api.delete(`/variables/${id}`);
      await fetchVariaveis();
    } catch (error) {
      console.error('[DRE] Erro ao excluir variável:', error);
      alert(error.response?.data?.error || 'Não foi possível excluir a variável.');
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    const range = getPresetRange(preset, anoSelecionado);

    setDateInicio(range.start);
    setDateFim(range.end);
    setActivePreset(preset);
  };

  const anosDisponiveis = useMemo(() => {
    const anos = new Set([anoAtual, anoAtual - 1, anoAtual + 1]);

    ordens.forEach((os) => {
      const data = new Date(getDataOS(os));
      if (!Number.isNaN(data.getTime())) anos.add(data.getFullYear());
    });

    return Array.from(anos).sort((a, b) => b - a);
  }, [ordens, anoAtual]);

  return (
    <div className="dre-page">
      <GlobalStyles />

      <header className="dre-top">
        <div className="dre-top-left">
          {onBack ? (
            <button type="button" className="plain-btn" onClick={onBack}>
              <Icons.ArrowLeft />
              Voltar
            </button>
          ) : null}

          <div>
            <span className="kicker">Lumix Óticas</span>
            <h1>DRE operacional</h1>
            <p>Resultado da ótica com receita real das OS, CPV, despesas e margem.</p>
          </div>
        </div>

        <div className="dre-top-actions">
          <button type="button" className="plain-icon-btn" onClick={fetchAll} title="Atualizar DRE">
            <Icons.Refresh />
          </button>
        </div>
      </header>

      <section className="filters-line">
        <select
          value={anoSelecionado}
          onChange={(e) => setAnoSelecionado(e.target.value)}
        >
          {anosDisponiveis.map((ano) => (
            <option key={ano} value={ano}>
              Exercício {ano}
            </option>
          ))}
        </select>

        <select
          value={empresaSelecionada}
          onChange={(e) => setEmpresaSelecionada(e.target.value)}
        >
          <option value="Todas">Consolidado da rede</option>
          {empresasFiltro.map((empresa) => (
            <option key={empresa.id || empresa.codigo} value={empresa.id || empresa.codigo}>
              {empresa.label}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={dateInicio}
          onChange={(e) => {
            setDateInicio(e.target.value);
            setActivePreset(null);
          }}
        />

        <input
          type="date"
          value={dateFim}
          onChange={(e) => {
            setDateFim(e.target.value);
            setActivePreset(null);
          }}
        />

        <div className="preset-group">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={activePreset === preset.id ? 'active' : ''}
              onClick={() => applyPreset(preset.id)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </section>

      {empresaSelecionada === 'Todas' ? (
        <section className="info-banner">
          <Icons.Alert />
          <div>
            <strong>Modo consolidado</strong>
            <span>Você está vendo a rede inteira. Para lançar despesas, selecione uma ótica específica.</span>
          </div>
        </section>
      ) : null}

      {loading ? (
        <ThinLoader />
      ) : (
        <>
          <section className="kpi-strip">
            <KpiCell
              icon={<Icons.Receipt />}
              label="Receita bruta"
              value={formatCurrency(kpi.receitaTotal)}
              help={`${kpi.qtdOS} contrato(s) · ticket ${formatCurrency(kpi.ticketMedio)}`}
              tone="blue"
            />

            <KpiCell
              icon={<Icons.Building />}
              label="CPV"
              value={formatCurrency(kpi.cpv)}
              help={`${kpi.cpvPercent.toFixed(1)}% da receita`}
              tone="orange"
            />

            <KpiCell
              icon={<Icons.Chart />}
              label="Lucro bruto"
              value={formatCurrency(kpi.lucroBruto)}
              help={`Margem bruta ${kpi.margemBruta.toFixed(1)}%`}
              tone="green"
            />

            <KpiCell
              icon={<Icons.Alert />}
              label="Despesas"
              value={formatCurrency(kpi.despesasOperacionais)}
              help="Operacionais + variáveis"
              tone="neutral"
            />

            <KpiCell
              icon={<Icons.Chart />}
              label="Resultado líquido"
              value={formatCurrency(kpi.lucroLiquido)}
              help={`Margem líquida ${kpi.margemLiquida.toFixed(1)}%`}
              tone={kpi.lucroLiquido >= 0 ? 'green' : 'red'}
            />
          </section>

          <section className="content-grid">
            <div className="chart-panel">
              <div className="panel-head">
                <div>
                  <h2>Receita x resultado</h2>
                  <p>{empresaAtualLabel} · {dateInicio} até {dateFim}</p>
                </div>
              </div>

              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height={290}>
                  <ComposedChart data={chartData} margin={{ top: 14, right: 10, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke="#edf0f3" vertical={false} />
                    <XAxis
                      dataKey="mes"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#5f6368', fontSize: 11, fontWeight: 600 }}
                    />
                    <YAxis
                      yAxisId="left"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#80868b', fontSize: 10 }}
                      tickFormatter={formatCompactCurrency}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#80868b', fontSize: 10 }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'Margem líquida') return [`${Number(value || 0).toFixed(1)}%`, name];
                        return [formatCurrency(value), name];
                      }}
                      labelStyle={{ color: '#202124', fontWeight: 700 }}
                      contentStyle={{
                        border: '1px solid #dadce0',
                        borderRadius: 12,
                        boxShadow: 'none',
                        fontSize: 12
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
                    <Bar
                      yAxisId="left"
                      dataKey="receitaBruta"
                      name="Receita bruta"
                      fill="#1a73e8"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={34}
                      onClick={(data) => abrirDetalhesMes(data.payload)}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="lucroLiquido"
                      name="Resultado líquido"
                      fill="#188038"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={34}
                      onClick={(data) => abrirDetalhesMes(data.payload)}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="margemLucro"
                      name="Margem líquida"
                      stroke="#f9ab00"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="variables-panel">
              <div className="panel-head">
                <div>
                  <h2>Variáveis da ótica</h2>
                  <p>Despesas extras que entram na DRE mensal.</p>
                </div>

                {empresaSelecionada !== 'Todas' ? (
                  <button
                    type="button"
                    className="plain-btn blue"
                    onClick={() => {
                      setVariavelEdit({ name: '', value: 0, valueInput: '', description: '' });
                      setModalVariavelAberto(true);
                    }}
                  >
                    <Icons.Plus />
                    Nova
                  </button>
                ) : null}
              </div>

              <div className="variables-list">
                {variaveisFiltradas.length ? (
                  variaveisFiltradas.map((variavel) => (
                    <div key={variavel._id} className="variable-row">
                      <div>
                        <strong>{variavel.name}</strong>
                        <span>{variavel.description || 'Sem descrição'}</span>
                      </div>

                      <div className="variable-actions">
                        <em>{formatCurrency(variavel.value)}</em>

                        {empresaSelecionada !== 'Todas' ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setVariavelEdit({
                                  ...variavel,
                                  value: parseNumber(variavel.value),
                                  valueInput: formatInputNumber(variavel.value)
                                });
                                setModalVariavelAberto(true);
                              }}
                            >
                              <Icons.Edit />
                            </button>

                            <button
                              type="button"
                              className="danger"
                              onClick={() => excluirVariavel(variavel._id)}
                            >
                              <Icons.Trash />
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-mini">
                    {empresaSelecionada === 'Todas'
                      ? 'Selecione uma ótica para cadastrar variáveis.'
                      : 'Nenhuma variável cadastrada.'}
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="dre-table-panel">
            <div className="panel-head">
              <div>
                <h2>Estrutura DRE para óticas</h2>
                <p>Clique no mês para lançar custos e despesas da unidade.</p>
              </div>
            </div>

            <div className="table-scroll">
              <table className="dre-table">
                <thead>
                  <tr>
                    <th className="sticky-col head-col">Conta</th>
                    {meses.map((mes) => (
                      <th
                        key={mes.mes}
                        className={empresaSelecionada !== 'Todas' ? 'month-clickable' : ''}
                        onClick={() => abrirDetalhesMes(mes)}
                      >
                        {mes.mes}
                        {empresaSelecionada !== 'Todas' ? <span>Editar</span> : null}
                      </th>
                    ))}
                    <th className="total-col">Acumulado</th>
                  </tr>
                </thead>

                <tbody>
                  <tr className="section-row">
                    <td colSpan={14}>Receita</td>
                  </tr>

                  <AccountRow
                    label="1. Receita bruta de vendas"
                    values={meses.map((m) => m.receitaBruta)}
                    total={kpi.receitaTotal}
                    className="master-row"
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="(-) Impostos sobre vendas"
                    values={meses.map((m) => m.impostos)}
                    total={kpi.impostos}
                    className="detail-row"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="= Receita líquida"
                    values={meses.map((m) => m.receitaLiquida)}
                    total={kpi.receitaLiquida}
                    className="subtotal-row"
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <tr className="section-row">
                    <td colSpan={14}>CPV · Custo dos produtos vendidos</td>
                  </tr>

                  <AccountRow
                    label="(-) Compra de armações"
                    values={meses.map((m) => m.custoArmacoes)}
                    total={soma(meses, 'custoArmacoes')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="(-) Fatura laboratório / lentes"
                    values={meses.map((m) => m.custoLentesLab)}
                    total={soma(meses, 'custoLentesLab')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="(-) Acessórios, LC e outros produtos"
                    values={meses.map((m) => m.custoOutros)}
                    total={soma(meses, 'custoOutros')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="= Lucro bruto"
                    values={meses.map((m) => m.lucroBruto)}
                    total={kpi.lucroBruto}
                    className="subtotal-row greenish"
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <PercentRow
                    label="Margem bruta"
                    values={meses.map((m) => m.margemBruta)}
                    total={kpi.margemBruta}
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <tr className="section-row">
                    <td colSpan={14}>Despesas operacionais</td>
                  </tr>

                  <AccountRow
                    label="(-) Comissões de vendas"
                    values={meses.map((m) => m.comissoes)}
                    total={soma(meses, 'comissoes')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="(-) Taxas cartão / maquininha"
                    values={meses.map((m) => m.taxasCartao)}
                    total={soma(meses, 'taxasCartao')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="(-) Folha salarial e encargos"
                    values={meses.map((m) => m.folhaPagamento)}
                    total={soma(meses, 'folhaPagamento')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="(-) Aluguel / condomínio / IPTU"
                    values={meses.map((m) => m.aluguel)}
                    total={soma(meses, 'aluguel')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="(-) Marketing / tráfego / campanhas"
                    values={meses.map((m) => m.marketing)}
                    total={soma(meses, 'marketing')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <AccountRow
                    label="(-) Energia, internet, sistemas e gerais"
                    values={meses.map((m) => m.despesasGerais)}
                    total={soma(meses, 'despesasGerais')}
                    className="detail-row indent"
                    isNegative
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  {variaveisFiltradas.map((variavel) => (
                    <AccountRow
                      key={variavel._id}
                      label={`(-) ${variavel.name}`}
                      values={meses.map((m) => m.variaveisDetalhe[variavel._id] || 0)}
                      total={meses.reduce((acc, m) => acc + Number(m.variaveisDetalhe[variavel._id] || 0), 0)}
                      className="detail-row indent variable"
                      isNegative
                      onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                    />
                  ))}

                  <AccountRow
                    label="= Resultado líquido"
                    values={meses.map((m) => m.lucroLiquido)}
                    total={kpi.lucroLiquido}
                    className="final-row"
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />

                  <PercentRow
                    label="Margem líquida"
                    values={meses.map((m) => m.margemLucro)}
                    total={kpi.margemLiquida}
                    onMonthClick={(index) => abrirDetalhesMes(meses[index])}
                  />
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {modalMesAberto && dadosMesEdit ? (
        <MesModal
          dados={dadosMesEdit}
          ano={anoSelecionado}
          empresaLabel={empresaAtualLabel}
          variaveis={variaveisFiltradas}
          saving={saving}
          onClose={() => setModalMesAberto(false)}
          onSave={salvarDreMes}
          onChange={handleInputChange}
          onVariableChange={handleVariavelModalChange}
        />
      ) : null}

      {modalVariavelAberto ? (
        <VariavelModal
          variavel={variavelEdit || {}}
          ano={anoSelecionado}
          empresaLabel={empresaAtualLabel}
          saving={saving}
          onClose={() => setModalVariavelAberto(false)}
          onSave={salvarVariavel}
          onChange={(patch) => setVariavelEdit((prev) => ({ ...(prev || {}), ...patch }))}
        />
      ) : null}
    </div>
  );
}

/* =============================================================
   Modais
============================================================= */

function MesModal({
  dados,
  ano,
  empresaLabel,
  variaveis,
  saving,
  onClose,
  onSave,
  onChange,
  onVariableChange
}) {
  return (
    <div className="modal-overlay">
      <div className="modal-shell month-modal">
        <div className="modal-head">
          <div>
            <span className="kicker">Lançamento mensal</span>
            <h2>{dados.mes} / {ano}</h2>
            <p>{empresaLabel}</p>
          </div>

          <button type="button" className="plain-icon-btn" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>

        <div className="modal-body">
          <div className="readonly-revenue">
            <span>Receita bruta vinda das OS</span>
            <strong>{formatCurrency(dados.receitaBruta)}</strong>
            <small>{dados.qtdOS} contrato(s) · ticket {formatCurrency(dados.ticketMedio)}</small>
          </div>

          <div className="modal-grid">
            <div className="input-group-block">
              <h3>Receita e CPV</h3>

              <MoneyInput label="Impostos sobre vendas" value={dados.impostosInput} onChange={(value) => onChange('impostos', value)} />
              <MoneyInput label="Fatura laboratório / lentes" value={dados.custoLentesLabInput} onChange={(value) => onChange('custoLentesLab', value)} />
              <MoneyInput label="Compra de armações" value={dados.custoArmacoesInput} onChange={(value) => onChange('custoArmacoes', value)} />
              <MoneyInput label="Acessórios, LC e outros" value={dados.custoOutrosInput} onChange={(value) => onChange('custoOutros', value)} />
            </div>

            <div className="input-group-block">
              <h3>Despesas da operação</h3>

              <MoneyInput label="Comissões de vendas" value={dados.comissoesInput} onChange={(value) => onChange('comissoes', value)} />
              <MoneyInput label="Taxas cartão / maquininha" value={dados.taxasCartaoInput} onChange={(value) => onChange('taxasCartao', value)} />
              <MoneyInput label="Folha salarial e encargos" value={dados.folhaPagamentoInput} onChange={(value) => onChange('folhaPagamento', value)} />
              <MoneyInput label="Aluguel / condomínio / IPTU" value={dados.aluguelInput} onChange={(value) => onChange('aluguel', value)} />
              <MoneyInput label="Marketing / anúncios" value={dados.marketingInput} onChange={(value) => onChange('marketing', value)} />
              <MoneyInput label="Energia, internet, sistemas e gerais" value={dados.despesasGeraisInput} onChange={(value) => onChange('despesasGerais', value)} />
            </div>

            {variaveis.length ? (
              <div className="input-group-block full">
                <h3>Variáveis personalizadas</h3>

                <div className="variables-modal-grid">
                  {variaveis.map((variavel) => (
                    <MoneyInput
                      key={variavel._id}
                      label={variavel.name}
                      value={dados.variaveisInputsRaw?.[variavel._id] ?? ''}
                      onChange={(value) => onVariableChange(variavel._id, value)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className={`result-box ${dados.lucroLiquido >= 0 ? 'positive-result' : 'negative-result'}`}>
            <div>
              <span>Resultado projetado</span>
              <strong>{formatCurrency(dados.lucroLiquido)}</strong>
            </div>
            <div>
              <span>Margem líquida</span>
              <strong>{Number(dados.margemLucro || 0).toFixed(2)}%</strong>
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="plain-btn" onClick={onClose} disabled={saving}>
            Cancelar
          </button>

          <button type="button" className="primary-btn" onClick={onSave} disabled={saving}>
            <Icons.Save />
            {saving ? 'Salvando...' : 'Salvar lançamentos'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MoneyInput({ label, value, onChange }) {
  return (
    <label className="money-input">
      <span>{label}</span>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0,00"
        inputMode="decimal"
      />
    </label>
  );
}

function VariavelModal({ variavel, ano, empresaLabel, saving, onClose, onSave, onChange }) {
  const isEditing = Boolean(variavel?._id);

  return (
    <div className="modal-overlay">
      <div className="modal-shell variable-modal">
        <div className="modal-head">
          <div>
            <span className="kicker">Variável da DRE</span>
            <h2>{isEditing ? 'Editar variável' : 'Nova variável'}</h2>
            <p>{empresaLabel} · {ano}</p>
          </div>

          <button type="button" className="plain-icon-btn" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>

        <div className="modal-body">
          <label className="form-field">
            <span>Nome da variável</span>
            <input
              value={variavel.name || ''}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="Ex.: Água, manutenção, software, limpeza..."
            />
          </label>

          {isEditing ? (
            <label className="form-field">
              <span>Valor base</span>
              <input
                type="text"
                value={variavel.valueInput ?? formatInputNumber(variavel.value)}
                onChange={(e) =>
                  onChange({
                    valueInput: e.target.value,
                    value: parseNumber(e.target.value)
                  })
                }
                placeholder="0,00"
                inputMode="decimal"
              />
            </label>
          ) : null}

          <label className="form-field">
            <span>Descrição</span>
            <input
              value={variavel.description || ''}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder="Descrição opcional"
            />
          </label>

          {!isEditing ? (
            <div className="soft-note">
              A variável nasce com valor zero. Depois você define o valor em cada mês no lançamento da DRE.
            </div>
          ) : null}
        </div>

        <div className="modal-foot">
          <button type="button" className="plain-btn" onClick={onClose} disabled={saving}>
            Cancelar
          </button>

          <button type="button" className="primary-btn" onClick={onSave} disabled={saving}>
            <Icons.Save />
            {saving ? 'Salvando...' : 'Salvar variável'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =============================================================
   CSS
============================================================= */

function GlobalStyles() {
  return (
    <style>{`
      :root {
        --dre-bg: #f8fafd;
        --dre-surface: #ffffff;
        --dre-surface-2: #f1f3f4;
        --dre-text: #202124;
        --dre-muted: #5f6368;
        --dre-soft: #80868b;
        --dre-border: #dadce0;
        --dre-border-soft: #edf0f3;
        --dre-blue: #1a73e8;
        --dre-green: #188038;
        --dre-red: #d93025;
        --dre-orange: #b06000;
        --dre-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif;
      }

      * {
        box-sizing: border-box;
      }

      .dre-page {
        min-height: 100vh;
        padding: 18px;
        color: var(--dre-text);
        background: var(--dre-bg);
        font-family: var(--dre-font);
        -webkit-font-smoothing: antialiased;
        text-rendering: geometricPrecision;
      }

      .dre-top {
        margin-bottom: 12px;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 14px;
      }

      .dre-top-left {
        display: flex;
        align-items: flex-start;
        gap: 10px;
      }

      .kicker {
        display: block;
        margin-bottom: 2px;
        color: var(--dre-muted);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .04em;
        text-transform: uppercase;
      }

      .dre-top h1 {
        margin: 0;
        color: var(--dre-text);
        font-size: 26px;
        line-height: 1.1;
        font-weight: 650;
        letter-spacing: -.035em;
      }

      .dre-top p {
        margin: 4px 0 0;
        color: var(--dre-muted);
        font-size: 13px;
      }

      .dre-top-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .plain-btn,
      .plain-icon-btn,
      .primary-btn {
        height: 34px;
        border: 1px solid var(--dre-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        background: #fff;
        font-size: 12px;
        font-weight: 650;
        font-family: inherit;
        cursor: pointer;
      }

      .plain-btn {
        padding: 0 11px;
        color: var(--dre-muted);
      }

      .plain-btn.blue {
        color: var(--dre-blue);
        border-color: #d2e3fc;
        background: #f8fbff;
      }

      .plain-icon-btn {
        width: 34px;
        padding: 0;
        color: var(--dre-muted);
        flex-shrink: 0;
      }

      .plain-btn:hover,
      .plain-icon-btn:hover {
        color: var(--dre-text);
        background: var(--dre-surface-2);
      }

      .primary-btn {
        padding: 0 14px;
        border-color: #1557b0;
        color: #fff;
        background: var(--dre-blue);
      }

      .primary-btn:disabled,
      .plain-btn:disabled {
        opacity: .65;
        cursor: not-allowed;
      }

      .filters-line {
        margin-bottom: 10px;
        padding: 9px;
        border: 1px solid var(--dre-border);
        border-radius: 13px;
        display: grid;
        grid-template-columns: 150px minmax(220px, 280px) 138px 138px minmax(340px, 1fr);
        gap: 8px;
        background: #fff;
      }

      .filters-line select,
      .filters-line input {
        height: 34px;
        padding: 0 10px;
        border: 1px solid var(--dre-border);
        border-radius: 999px;
        outline: 0;
        color: var(--dre-text);
        background: #fff;
        font-size: 12px;
        font-family: inherit;
      }

      .filters-line select:focus,
      .filters-line input:focus {
        border-color: var(--dre-blue);
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .preset-group {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 6px;
        overflow-x: auto;
      }

      .preset-group button {
        height: 30px;
        padding: 0 10px;
        border: 1px solid var(--dre-border);
        border-radius: 999px;
        color: var(--dre-muted);
        background: #fff;
        font-size: 11px;
        font-weight: 700;
        font-family: inherit;
        cursor: pointer;
        white-space: nowrap;
      }

      .preset-group button.active {
        color: var(--dre-blue);
        border-color: #d2e3fc;
        background: #f8fbff;
      }

      .info-banner {
        margin-bottom: 10px;
        padding: 10px 12px;
        border: 1px solid #fde68a;
        border-radius: 13px;
        display: flex;
        align-items: center;
        gap: 10px;
        color: #92400e;
        background: #fffbeb;
      }

      .info-banner strong {
        display: block;
        font-size: 12.5px;
      }

      .info-banner span {
        display: block;
        margin-top: 2px;
        font-size: 11.5px;
      }

      .kpi-strip {
        margin-bottom: 10px;
        border: 1px solid var(--dre-border);
        border-radius: 13px;
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        overflow: hidden;
        background: #fff;
      }

      .kpi-cell {
        min-height: 82px;
        padding: 12px;
        border-right: 1px solid var(--dre-border-soft);
        display: grid;
        grid-template-columns: 28px 1fr;
        grid-template-rows: auto auto auto;
        column-gap: 8px;
        align-content: center;
        background: #fff;
      }

      .kpi-cell:last-child {
        border-right: 0;
      }

      .kpi-icon {
        grid-row: 1 / span 3;
        width: 28px;
        height: 28px;
        border: 1px solid var(--dre-border);
        border-radius: 9px;
        display: grid;
        place-items: center;
        color: var(--dre-blue);
        background: #f8fafd;
      }

      .kpi-cell.green .kpi-icon {
        color: var(--dre-green);
        background: #f1f8f4;
        border-color: #cce8d5;
      }

      .kpi-cell.red .kpi-icon {
        color: var(--dre-red);
        background: #fef2f2;
        border-color: #fecaca;
      }

      .kpi-cell.orange .kpi-icon {
        color: var(--dre-orange);
        background: #fff7ed;
        border-color: #fed7aa;
      }

      .kpi-cell.blue .kpi-icon {
        color: var(--dre-blue);
        background: #eef5ff;
        border-color: #d2e3fc;
      }

      .kpi-cell span {
        color: var(--dre-muted);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .02em;
        text-transform: uppercase;
      }

      .kpi-cell strong {
        margin-top: 3px;
        color: var(--dre-text);
        font-size: 18px;
        line-height: 1.15;
        font-weight: 700;
        letter-spacing: -.025em;
      }

      .kpi-cell small {
        margin-top: 3px;
        color: var(--dre-soft);
        font-size: 11px;
        font-weight: 500;
      }

      .content-grid {
        margin-bottom: 10px;
        display: grid;
        grid-template-columns: minmax(0, 1.35fr) minmax(320px, .65fr);
        gap: 10px;
      }

      .chart-panel,
      .variables-panel,
      .dre-table-panel {
        border: 1px solid var(--dre-border);
        border-radius: 13px;
        overflow: hidden;
        background: #fff;
      }

      .panel-head {
        min-height: 54px;
        padding: 12px;
        border-bottom: 1px solid var(--dre-border-soft);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .panel-head h2 {
        margin: 0;
        color: var(--dre-text);
        font-size: 14px;
        font-weight: 750;
        letter-spacing: -.01em;
      }

      .panel-head p {
        margin: 3px 0 0;
        color: var(--dre-muted);
        font-size: 11.5px;
      }

      .chart-wrap {
        padding: 10px 10px 4px;
      }

      .variables-list {
        max-height: 344px;
        overflow: auto;
      }

      .variable-row {
        min-height: 56px;
        padding: 9px 12px;
        border-bottom: 1px solid var(--dre-border-soft);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }

      .variable-row strong {
        display: block;
        color: var(--dre-text);
        font-size: 12.5px;
        font-weight: 750;
      }

      .variable-row span {
        display: block;
        margin-top: 2px;
        color: var(--dre-muted);
        font-size: 11px;
      }

      .variable-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        min-width: 150px;
      }

      .variable-actions em {
        color: var(--dre-muted);
        min-width: 88px;
        font-size: 11px;
        text-align: right;
        font-style: normal;
        font-weight: 700;
        white-space: nowrap;
      }

      .variable-actions button {
        width: 28px;
        height: 28px;
        border: 1px solid var(--dre-border);
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: var(--dre-muted);
        background: #fff;
        cursor: pointer;
      }

      .variable-actions button:hover {
        color: var(--dre-blue);
        background: #f8fbff;
        border-color: #d2e3fc;
      }

      .variable-actions button.danger:hover {
        color: var(--dre-red);
        background: #fef2f2;
        border-color: #fecaca;
      }

      .empty-mini {
        min-height: 160px;
        padding: 20px;
        display: grid;
        place-items: center;
        color: var(--dre-muted);
        font-size: 12px;
        text-align: center;
      }

      .table-scroll {
        max-height: calc(100vh - 410px);
        overflow: auto;
      }

      .table-scroll::-webkit-scrollbar,
      .variables-list::-webkit-scrollbar,
      .preset-group::-webkit-scrollbar,
      .modal-body::-webkit-scrollbar {
        width: 7px;
        height: 7px;
      }

      .table-scroll::-webkit-scrollbar-thumb,
      .variables-list::-webkit-scrollbar-thumb,
      .preset-group::-webkit-scrollbar-thumb,
      .modal-body::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: rgba(95, 99, 104, .28);
      }

      .dre-table {
        width: 100%;
        min-width: 1360px;
        border-collapse: collapse;
        font-variant-numeric: tabular-nums;
      }

      .dre-table th {
        position: sticky;
        top: 0;
        z-index: 3;
        height: 40px;
        padding: 0 10px;
        border-bottom: 1px solid var(--dre-border);
        color: var(--dre-muted);
        background: #f8fafd;
        font-size: 10.5px;
        font-weight: 800;
        letter-spacing: .04em;
        text-align: right;
        text-transform: uppercase;
        white-space: nowrap;
      }

      .dre-table th:first-child {
        text-align: left;
      }

      .dre-table td {
        height: 38px;
        padding: 0 10px;
        border-bottom: 1px solid var(--dre-border-soft);
        color: var(--dre-text);
        background: #fff;
        font-size: 12px;
        text-align: right;
        white-space: nowrap;
      }

      .dre-table th:not(.sticky-col),
      .dre-table td:not(.sticky-col) {
        min-width: 94px;
      }

      .dre-table td span {
        display: inline-block;
        min-width: max-content;
      }

      .dre-table td:first-child {
        text-align: left;
      }

      .sticky-col {
        position: sticky;
        left: 0;
        z-index: 2;
        min-width: 260px;
        background: inherit !important;
        box-shadow: inset -1px 0 0 var(--dre-border-soft);
      }

      .head-col {
        z-index: 4;
        background: #f8fafd !important;
      }

      .total-col {
        min-width: 150px;
        background: #f8fafd !important;
        font-weight: 800;
        box-shadow: inset 1px 0 0 var(--dre-border-soft);
      }

      .month-clickable {
        cursor: pointer;
      }

      .month-clickable:hover {
        color: var(--dre-blue);
        background: #eef5ff;
      }

      .month-clickable span {
        display: block;
        margin-top: 1px;
        color: var(--dre-blue);
        font-size: 8.5px;
        font-weight: 800;
      }

      .section-row td {
        height: 32px;
        color: var(--dre-muted);
        background: #f8fafd;
        font-size: 10.5px;
        font-weight: 850;
        letter-spacing: .08em;
        text-align: left;
        text-transform: uppercase;
      }

      .master-row td {
        font-weight: 800;
        color: var(--dre-blue);
      }

      .detail-row td {
        color: #3c4043;
      }

      .detail-row.indent td:first-child {
        padding-left: 22px;
        color: var(--dre-muted);
      }

      .detail-row.variable td:first-child {
        color: #7e22ce;
      }

      .subtotal-row td {
        background: #fbfcfe;
        font-weight: 800;
      }

      .subtotal-row.greenish td {
        color: var(--dre-green);
      }

      .final-row td {
        height: 42px;
        border-top: 2px solid var(--dre-border);
        background: #f8fafd;
        font-weight: 900;
      }

      .row-percent td {
        color: var(--dre-muted);
        font-weight: 750;
      }

      .positive {
        color: var(--dre-green) !important;
        font-weight: 800;
      }

      .negative {
        color: var(--dre-red) !important;
        font-weight: 800;
      }

      .warning {
        color: #b06000 !important;
        font-weight: 800;
      }

      .dre-loading {
        min-height: 420px;
        border: 1px solid var(--dre-border);
        border-radius: 13px;
        display: grid;
        place-items: center;
        align-content: center;
        gap: 9px;
        color: var(--dre-muted);
        background: #fff;
        font-size: 13px;
      }

      .thin-loader {
        width: 190px;
        height: 3px;
        border-radius: 999px;
        overflow: hidden;
        background: #e8eaed;
        position: relative;
      }

      .thin-loader::after {
        content: "";
        position: absolute;
        inset: 0;
        width: 40%;
        border-radius: inherit;
        background: var(--dre-blue);
        animation: loader-move 1.1s linear infinite;
      }

      @keyframes loader-move {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(260%); }
      }

      .modal-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        padding: 20px;
        display: grid;
        place-items: center;
        background: rgba(32, 33, 36, .60);
      }

      .modal-shell {
        width: min(760px, 96vw);
        max-height: 92vh;
        border: 1px solid var(--dre-border);
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: #fff;
      }

      .variable-modal {
        width: min(520px, 96vw);
      }

      .modal-head {
        padding: 14px;
        border-bottom: 1px solid var(--dre-border-soft);
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
      }

      .modal-head h2 {
        margin: 0;
        color: var(--dre-text);
        font-size: 18px;
        font-weight: 750;
        letter-spacing: -.02em;
      }

      .modal-head p {
        margin: 3px 0 0;
        color: var(--dre-muted);
        font-size: 12px;
      }

      .modal-body {
        padding: 14px;
        overflow: auto;
      }

      .readonly-revenue {
        margin-bottom: 12px;
        padding: 12px;
        border: 1px solid #d2e3fc;
        border-radius: 13px;
        display: grid;
        gap: 3px;
        background: #f8fbff;
      }

      .readonly-revenue span {
        color: var(--dre-muted);
        font-size: 11px;
        font-weight: 750;
        text-transform: uppercase;
      }

      .readonly-revenue strong {
        color: var(--dre-blue);
        font-size: 22px;
        font-weight: 850;
      }

      .readonly-revenue small {
        color: var(--dre-muted);
        font-size: 11px;
      }

      .modal-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
      }

      .input-group-block {
        padding: 12px;
        border: 1px solid var(--dre-border);
        border-radius: 13px;
        background: #fff;
      }

      .input-group-block.full {
        grid-column: 1 / -1;
      }

      .input-group-block h3 {
        margin: 0 0 10px;
        color: var(--dre-text);
        font-size: 13px;
        font-weight: 800;
      }

      .money-input {
        min-height: 35px;
        border-bottom: 1px solid var(--dre-border-soft);
        display: grid;
        grid-template-columns: minmax(0, 1fr) 130px;
        align-items: center;
        gap: 8px;
      }

      .money-input:last-child {
        border-bottom: 0;
      }

      .money-input span {
        color: var(--dre-muted);
        font-size: 11.5px;
        font-weight: 650;
      }

      .money-input input {
        height: 28px;
        padding: 0 8px;
        border: 1px solid var(--dre-border);
        border-radius: 8px;
        outline: 0;
        color: var(--dre-text);
        background: #fff;
        font-size: 12px;
        font-family: inherit;
        font-weight: 700;
        text-align: right;
      }

      .money-input input:focus,
      .form-field input:focus {
        border-color: var(--dre-blue);
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .variables-modal-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0 12px;
      }

      .result-box {
        margin-top: 12px;
        padding: 12px;
        border-radius: 13px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
      }

      .result-box span {
        display: block;
        color: var(--dre-muted);
        font-size: 11px;
        font-weight: 750;
        text-transform: uppercase;
      }

      .result-box strong {
        display: block;
        margin-top: 4px;
        color: var(--dre-text);
        font-size: 20px;
        font-weight: 850;
      }

      .positive-result {
        background: #f1f8f4;
        border: 1px solid #cce8d5;
      }

      .positive-result strong {
        color: var(--dre-green);
      }

      .negative-result {
        background: #fef2f2;
        border: 1px solid #fecaca;
      }

      .negative-result strong {
        color: var(--dre-red);
      }

      .modal-foot {
        padding: 12px 14px;
        border-top: 1px solid var(--dre-border-soft);
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      }

      .form-field {
        margin-bottom: 10px;
        display: grid;
        gap: 5px;
      }

      .form-field span {
        color: var(--dre-muted);
        font-size: 11px;
        font-weight: 750;
        text-transform: uppercase;
      }

      .form-field input {
        height: 36px;
        padding: 0 10px;
        border: 1px solid var(--dre-border);
        border-radius: 10px;
        outline: 0;
        color: var(--dre-text);
        background: #fff;
        font-size: 12.5px;
        font-family: inherit;
      }

      .form-field input[inputmode="decimal"] {
        font-weight: 750;
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .soft-note {
        margin-top: 8px;
        padding: 10px;
        border: 1px solid #d2e3fc;
        border-radius: 11px;
        color: var(--dre-blue);
        background: #f8fbff;
        font-size: 12px;
        line-height: 1.4;
      }

      @media (max-width: 1180px) {
        .filters-line {
          grid-template-columns: 1fr 1fr;
        }

        .preset-group {
          grid-column: 1 / -1;
        }

        .kpi-strip {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .content-grid {
          grid-template-columns: 1fr;
        }

        .table-scroll {
          max-height: none;
        }
      }

      @media (max-width: 760px) {
        .dre-page {
          padding: 10px;
        }

        .dre-top {
          align-items: stretch;
          flex-direction: column;
        }

        .dre-top-left {
          flex-direction: column;
          gap: 8px;
        }

        .filters-line {
          grid-template-columns: 1fr;
        }

        .preset-group {
          grid-column: auto;
        }

        .kpi-strip {
          grid-template-columns: 1fr;
        }

        .kpi-cell {
          border-right: 0;
          border-bottom: 1px solid var(--dre-border-soft);
        }

        .kpi-cell:last-child {
          border-bottom: 0;
        }

        .modal-grid,
        .variables-modal-grid,
        .result-box {
          grid-template-columns: 1fr;
        }

        .money-input {
          grid-template-columns: 1fr;
          padding: 7px 0;
        }

        .money-input input {
          width: 100%;
          text-align: left;
        }
      }
    `}</style>
  );
}
